import {
  AddCircleOutline,
  ChatBubbleOutline,
  CheckBoxRounded,
  DisabledByDefaultRounded,
  EditOutlined,
  HistoryRounded,
  MailOutline,
  NotificationsActiveOutlined,
  OutlinedFlagOutlined,
  ReportProblemOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Box, Button, Chip, Divider, Tooltip } from "@mui/material";
import { amber, green, red } from "@mui/material/colors";
import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MasterDetailModule,
  MultiFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  SideBarModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil, size } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { getDaysAgo } from "../../../../_src_shared/utils/functions/getDaysAgo";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import CommentModal, {
  COMMENT_TYPES,
} from "../../../../components/comments/CommentModal";
import FieldUpdateModal, {
  UPDATE_TYPES,
} from "../../../../components/forms/FieldUpdateModal";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import SearchInput from "../../../../components/inputs/SearchInput";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";

// Slate-leaning neutral palette (no MUI grey[] for borders/text/backgrounds)
const panel = {
  surface: "#ffffff",
  surfaceMuted: "#fcfdfe",
  background: "#f8fafc",
  border: "#e2e8f0",
  borderSubtle: "#eef2f7",
  borderFaint: "#e1e4e869",
  text: "#0f172a",
  textStrong: "#1e293b",
  textBody: "#334155",
  textMuted: "#475569",
  textLabel: "#64748b",
  textFaint: "#94a3b8",
  avatarBg: "#eef2ff",
  avatarFg: "#4f46e5",
};

ModuleRegistry.registerModules([
  SideBarModule,
  RowGroupingPanelModule,
  MultiFilterModule,
  RowGroupingModule,
  PivotModule,
  TreeDataModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
  MasterDetailModule,
]);

// Risk-bucket helper descriptions (sourced from EXCEPTIONS.md §7)
const RISK_BUCKET_DESCRIPTIONS = {
  "Collateral Quality":
    "What you're lending against — property type, condition, location, valuation risk.",
  "Credit Structure":
    "How much you're lending — leverage, pricing, loan size, term structure.",
  "Market Risk":
    "Where you're lending — market liquidity, geographic and segment exposure.",
  "Borrower Profile":
    "Who you're lending to — experience, liquidity, credit history, FICO.",
  "Legal & Structural Risk":
    "How well-protected the loan is — entity, title, lien position, documentation.",
};

const BLOCKS_DEAL_TOOLTIP =
  "When enabled, this hard exception signals the deal cannot proceed as currently structured. The processing team and loan officer should be notified before continuing — typically because terms, leverage, or borrower profile fall outside program guidelines.";

const DEAL_DEAD_TOOLTIP =
  "When enabled, this hard exception indicates the deal is dead due to this guideline deviation. Reserved for cases where no workable path forward exists.";

const EMAIL_ON_TOOLTIP =
  "Notification intent ON. On update, an email is queued for the assignee via the exception_updates broadcast — actual delivery depends on the notification worker (no in-process send today).";

const EMAIL_OFF_TOOLTIP =
  "Notification intent OFF. No email notification will be queued on update. Edit to enable.";

// Configs for the focused field-update modals. Keyed lookups so the renderer
// just stores which one (if any) is open and reads everything from here.
const FIELD_EDIT_CONFIGS = {
  description: {
    title: "Edit Description",
    // description:
    //   "The human-written summary of what the exception covers. Appears in the row header and the description card.",
    fields: [
      {
        name: "exceptionDescription",
        label: "Description",
        type: "multiline",
        placeholder: "Describe the exception…",
        rows: 5,
        required: true,
      },
    ],
  },
  compensatingFactors: {
    title: "Edit Compensating Factors",
    // description:
    //   "Risk-mitigating context that approvers should weigh against the deviation. Approvers read this first.",
    fields: [
      {
        name: "compensatingFactors",
        label: "Compensating Factors",
        type: "multiline",
        placeholder: "What offsets the risk?",
        rows: 5,
      },
    ],
  },
};

const STATUS_TONE = {
  Open: { fg: panel.textBody, bg: panel.borderSubtle, bar: panel.textFaint },
  "In Review": { fg: amber[900], bg: amber[50], bar: amber[500] },
  "Pending Approval": { fg: amber[900], bg: amber[50], bar: amber[500] },
  Approved: { fg: green[800], bg: green[50], bar: green[500] },
  Rejected: { fg: red[800], bg: red[50], bar: red[500] },
  Resolved: { fg: green[800], bg: green[50], bar: green[500] },
};

const getStatusTone = (status) =>
  STATUS_TONE[status] || {
    fg: panel.textBody,
    bg: panel.borderSubtle,
    bar: panel.textFaint,
  };

const LoanDrilldownExceptionsRedesignTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const exceptions = useSelector(
    (state) => state?.loanDrilldown?.exceptions || [],
  );
  const users = useSelector((state) => state.users);
  const gridRef = useRef(null);
  const wrapperRef = useRef(null);

  const [quickFilter, setQuickFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [wrapperWidth, setWrapperWidth] = useState(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width) setWrapperWidth(Math.floor(width));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleAdd = useCallback(() => {
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
    dispatch(sidebarSetStateValue(null));
  }, [dispatch]);

  const handleEdit = useCallback(
    (exception) => {
      dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
      dispatch(sidebarSetStateValue(exception));
    },
    [dispatch],
  );

  const statusCounts = useMemo(() => {
    const counts = { All: exceptions.length, Hard: 0 };
    exceptions.forEach((e) => {
      const s = e?.exceptionStatus || "Open";
      counts[s] = (counts[s] || 0) + 1;
      if (e?.isHardException) counts.Hard += 1;
    });
    return counts;
  }, [exceptions]);

  const statusOrder = useMemo(() => {
    const seen = new Set();
    const ordered = ["All"];
    exceptions.forEach((e) => {
      const s = e?.exceptionStatus || "Open";
      if (!seen.has(s)) {
        seen.add(s);
        ordered.push(s);
      }
    });
    if (statusCounts.Hard > 0) ordered.push("Hard");
    return ordered;
  }, [exceptions, statusCounts.Hard]);

  const filteredExceptions = useMemo(() => {
    if (isNil(exceptions)) return [];
    if (statusFilter === "All") return exceptions;
    if (statusFilter === "Hard")
      return exceptions.filter((e) => !!e?.isHardException);
    return exceptions.filter(
      (e) => (e?.exceptionStatus || "Open") === statusFilter,
    );
  }, [exceptions, statusFilter]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Exception",
        field: "exceptionDescription",
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: ExceptionSummaryCell,
        },
        flex: 3,
        minWidth: 320,
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          overflow: "hidden",
          paddingTop: "14px",
          paddingBottom: "14px",
          lineHeight: "1.45",
        },
      },
      {
        headerName: "Comp. Factors",
        field: "compensatingFactors",
        cellRenderer: CompFactorsCell,
        flex: 2,
        minWidth: 200,
        cellStyle: {
          overflow: "hidden",
          paddingTop: "14px",
          paddingBottom: "14px",
          lineHeight: "1.45",
        },
      },
      {
        headerName: "Risk Bucket",
        field: "riskBucket",
        cellRenderer: RiskBucketCell,
        flex: 1,
        minWidth: 130,
        maxWidth: 180,
      },
      {
        headerName: "Assigned",
        field: "assignedTo",
        cellRenderer: AssignedCell,
        cellRendererParams: { users },
        flex: 1,
        minWidth: 130,
        maxWidth: 200,
      },
      {
        headerName: "Status",
        field: "exceptionStatus",
        cellRenderer: StatusCell,
        flex: 0,
        width: 170,
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
      },
    ],
    [users],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
      cellClass: "ag-cell-vertical-center",
    }),
    [],
  );

  const columnTypes = useMemo(
    () => ({
      ...columnTypesDate(),
    }),
    [],
  );

  const detailCellRendererParams = useMemo(
    () => ({
      onEdit: handleEdit,
      users,
      maxWidth: wrapperWidth,
    }),
    [handleEdit, users, wrapperWidth],
  );

  const detailCellRenderer = useCallback(ExceptionDetailRenderer, []);

  const handleRowClicked = useCallback((params) => {
    if (!params?.node) return;
    params.node.setExpanded(!params.node.expanded);
  }, []);

  const getRowId = useCallback((params) => String(params.data?._id || ""), []);

  if (isEmpty(exceptions)) {
    return (
      <EmptyStatePlaceholder
        icon={<WarningAmberOutlined />}
        title="No exceptions on this loan"
        description="Log any hard or soft exceptions with compensating factors and a resolution plan related to this loan."
        actions={
          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={handleAdd}
          >
            Add Exception
          </Button>
        }
      />
    );
  }

  const totalCount = size(exceptions);

  return (
    <PrimaryContentCard
      maxWidth="xl"
      title={`Loan Exceptions (${totalCount})`}
      subTitle="Expand any row to see compensating factors, comments, and full details."
      endContent={
        <Flx ac g={1}>
          <Button
            size="medium"
            variant="outlined"
            startIcon={<HistoryRounded sx={{ fontSize: 14 }} />}
            onClick={() => navigate("../exceptions")}
            sx={{
              textTransform: "none",
              fontSize: 11.5,
              fontWeight: 500,
              color: panel.textMuted,
              borderColor: panel.border,
              "&:hover": {
                borderColor: panel.textFaint,
                background: panel.surface,
              },
            }}
          >
            View Legacy
          </Button>
          <Button
            variant="contained"
            startIcon={
              <AddCircleOutline sx={{ fontSize: "18px !important" }} />
            }
            onClick={handleAdd}
            size="medium"
          >
            Add Exception
          </Button>
        </Flx>
      }
    >
      <Flx column g={2} sx={{ mt: 1 }}>
        <Flx ac wrap g={1.5}>
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder="Search exceptions…"
          />
          <StatusChipStrip
            order={statusOrder}
            counts={statusCounts}
            active={statusFilter}
            onChange={setStatusFilter}
          />

          {/* <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder="Search exceptions…"
          /> */}
        </Flx>

        <Box
          ref={wrapperRef}
          sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}
        >
          <AgGridTableWrapper compactTable suppressMinHeightWhenPopulated>
            <AgGridReact
              ref={gridRef}
              rowData={filteredExceptions}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              columnTypes={columnTypes}
              getRowId={getRowId}
              quickFilterText={quickFilter}
              domLayout="autoHeight"
              masterDetail
              detailCellRenderer={detailCellRenderer}
              detailCellRendererParams={detailCellRendererParams}
              detailRowAutoHeight
              keepDetailRows
              onRowClicked={handleRowClicked}
            />
          </AgGridTableWrapper>
        </Box>

        {/* <Flx jc center sx={{ pt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryRounded sx={{ fontSize: 14 }} />}
            onClick={() => navigate("../exceptions")}
            sx={{
              textTransform: "none",
              fontSize: 11.5,
              fontWeight: 500,
              color: panel.textMuted,
              borderColor: panel.border,
              "&:hover": {
                borderColor: panel.textFaint,
                background: panel.surface,
              },
            }}
          >
            View Legacy Table
          </Button>
        </Flx> */}
      </Flx>
    </PrimaryContentCard>
  );
};

/* ─── Status chip strip (compact pill row) ─── */

const StatusChipStrip = ({ order, counts, active, onChange }) => (
  <Flx ac wrap g={0.75}>
    {order.map((s) => {
      const isActive = active === s;
      const tone = s === "All" || s === "Hard" ? null : getStatusTone(s);
      const accent = s === "Hard" ? red[600] : tone?.bar || panel.textBody;
      const count = counts[s] ?? 0;
      return (
        <Box
          key={s}
          component="button"
          type="button"
          onClick={() => onChange(s)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            px: 2,
            py: 1.25,
            borderRadius: 1.5,
            fontSize: 11.5,
            fontWeight: 600,
            cursor: "pointer",
            border: `1px solid ${isActive ? accent : panel.border}`,
            background: isActive ? accent : "#fff",
            color: isActive ? "#fff" : panel.textStrong,
            transition: "background 0.1s, border-color 0.1s",
            "&:hover": { borderColor: accent },
          }}
        >
          {s}
          <Box
            component="span"
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              color: isActive ? "rgba(255,255,255,0.85)" : panel.textLabel,
            }}
          >
            {count}
          </Box>
        </Box>
      );
    })}
  </Flx>
);

/* ─── Cell renderers ─── */

const ExceptionSummaryCell = ({ data }) => {
  if (!data) return null;
  const isHardException = !!data?.isHardException;
  const commentCount = size(data?.comments);
  const description =
    data?.exceptionDescription ||
    (isHardException ? "Hard exception" : "Soft exception");

  return (
    <Flx column g={0.25} sx={{ py: 0.25, minWidth: 0, width: "100%" }}>
      <Flx ac g={0.75} wrap>
        {isHardException && (
          <ReportProblemOutlined sx={{ fontSize: 12, color: red[500] }} />
        )}
        {data?.exceptionCategory && (
          <Txt
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.5,
              color: panel.textLabel,
              textTransform: "uppercase",
            }}
          >
            {data.exceptionCategory}
          </Txt>
        )}
        {commentCount > 0 && (
          <Flx ac g={0.25} sx={{ color: amber[700] }}>
            <ChatBubbleOutline sx={{ fontSize: 11 }} />
            <Txt sx={{ fontSize: 10.5, color: amber[800] }}>{commentCount}</Txt>
          </Flx>
        )}
      </Flx>
      <Txt
        sx={{
          fontSize: 13,
          color: panel.text,
          lineHeight: 1.45,
          minWidth: 0,
          // Wrap up to 3 lines, ellipsize the rest. `pre-wrap` so source
          // newlines/tabs from the textarea-edited description survive.
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {description}
      </Txt>
    </Flx>
  );
};

// Compensating factors preview — 2-line clamp with ellipsis, preserves
// textarea newlines so the snippet reads like the detail view in miniature.
const CompFactorsCell = ({ value }) => {
  if (!value) {
    return <Txt sx={{ fontSize: 11, color: panel.textFaint }}>—</Txt>;
  }
  return (
    <Txt
      sx={{
        fontSize: 12,
        color: panel.textBody,
        lineHeight: 1.45,
        minWidth: 0,
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Txt>
  );
};

const RiskBucketCell = ({ value }) =>
  value ? (
    <Chip
      size="small"
      variant="outlined"
      label={value}
      icon={<OutlinedFlagOutlined sx={{ fontSize: "11px !important" }} />}
      sx={{
        height: 20,
        fontSize: 10.5,
        background: "#f1f5f9",
        maxWidth: "100%",
        px: 0.5,
      }}
    />
  ) : (
    <Txt sx={{ fontSize: 11, color: panel.textFaint }}>—</Txt>
  );

const AssignedCell = ({ value, users }) => {
  const name = value ? getUserFullNameFromEmail(users, value) : null;
  if (!name)
    return <Txt sx={{ fontSize: 11, color: panel.textFaint }}>Unassigned</Txt>;
  return (
    <Flx ac g={0.75} sx={{ minWidth: 0 }}>
      <Avatar name={name} />
      <Txt
        sx={{
          fontSize: 11.5,
          color: panel.textStrong,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name.split(" ")[0]}
      </Txt>
    </Flx>
  );
};

const StatusCell = ({ value, data }) => {
  const status = value || "Open";
  const tone = getStatusTone(status);
  // Resolution date is conditional — only meaningful on Approved/Rejected
  // (per LoanException Display Guide §2). Stack it under the chip when set
  // so the Resolution column doesn't need to exist on its own.
  const isResolved =
    status === "Approved" || status === "Rejected" || status === "Resolved";
  const resolutionDate =
    isResolved && data?.resolutionDateTime
      ? formatDate(data.resolutionDateTime)
      : null;
  return (
    <Flx column sx={{ alignItems: "flex-start", gap: 0.25 }}>
      <Chip
        size="small"
        label={status}
        icon={
          <Box
            component="span"
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: tone.bar,
              ml: "8px !important",
            }}
          />
        }
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 600,
          color: tone.fg,
          background: tone.bg,
          border: `1px solid ${tone.bar}33`,
        }}
      />
      {resolutionDate && (
        <Txt sx={{ fontSize: 10.5, color: panel.textLabel, lineHeight: 1.3 }}>
          Resolved {resolutionDate}
        </Txt>
      )}
    </Flx>
  );
};

/* ─── Master/detail expanded row ─── */

const ExceptionDetailRenderer = ({ data, onEdit, users, maxWidth }) => {
  const dealId = useSelector((state) => state?.loanDrilldown?._id);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  // null when closed; otherwise one of the FIELD_EDIT_CONFIGS keys ("description" | "compensatingFactors")
  const [fieldEditKey, setFieldEditKey] = useState(null);
  const isHardException = !!data?.isHardException;
  const updatedLabel = useMemo(() => {
    if (!data?.updateDateTime) return null;
    const days = getDaysAgo(data.updateDateTime);
    if (days === 0) return "Today";
    if (days === 1) return "1d ago";
    if (days > 60) return formatDate(data.updateDateTime);
    return `${days}d ago`;
  }, [data?.updateDateTime]);

  // Sort most-recent first. Backend `$push`es comments oldest-first per
  // EXCEPTIONS.md §11.3, so we reverse the timeline for display. Falls back
  // through the date field aliases used across comment shapes.
  const comments = useMemo(() => {
    const raw = data?.comments || [];
    const tsOf = (c) =>
      new Date(
        c?.commentDateTime || c?.createDateTime || c?.createdAt || 0,
      ).getTime() || 0;
    return [...raw].sort((a, b) => tsOf(b) - tsOf(a));
  }, [data?.comments]);

  return (
    <Box
      role="gridcell"
      className="full-width-panel"
      sx={{
        position: "sticky",
        left: 0,
        width: maxWidth ? `${maxWidth}px` : "100%",
        maxWidth: maxWidth ? `${maxWidth}px` : "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        // px: 3,
        // py: 2.5,
        // px: 2.5,
        // pb: 6,
        background: "#fcfdfe",
        borderTop: `1px solid #e1e4e869`,
        borderBottom: `5px solid #e1e4e869`,
      }}
    >
      <Flx
        column
        g={2}
        sx={{
          width: "calc(100% - 44px)",
          minWidth: 0,
          borderLeft: `4px solid #e1e4e869`,
          px: 2.5,
          pl: 3,
          ml: "22px",
          py: 2.5,
          pb: 6,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 2fr) minmax(0, 1fr)",
            },
            gap: 4,
            alignItems: "start",
            width: "100%",
          }}
        >
          <Flx column g={3.5}>
            <DetailCard
              title="Description"
              endAdornment={
                <InlineEditButton
                  label="Edit"
                  onClick={() => setFieldEditKey("description")}
                />
              }
            >
              {data?.exceptionDescription ? (
                <Txt
                  sx={{
                    fontSize: 13,
                    color: panel.textStrong,
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    // Preserve textarea formatting (newlines + tabs) so the
                    // display matches the edit form's layout.
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {data.exceptionDescription}
                </Txt>
              ) : (
                <Txt
                  sx={{
                    fontSize: 12.5,
                    color: panel.textFaint,
                    fontStyle: "italic",
                  }}
                >
                  No description provided.
                </Txt>
              )}
            </DetailCard>

            <DetailCard
              title="Compensating Factors"
              endAdornment={
                <InlineEditButton
                  label="Edit"
                  onClick={() => setFieldEditKey("compensatingFactors")}
                />
              }
            >
              {data?.compensatingFactors ? (
                <Txt
                  sx={{
                    fontSize: 13,
                    color: panel.textStrong,
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {data.compensatingFactors}
                </Txt>
              ) : (
                <Txt
                  sx={{
                    fontSize: 12.5,
                    color: panel.textFaint,
                    fontStyle: "italic",
                  }}
                >
                  No compensating factors documented.
                </Txt>
              )}
            </DetailCard>

            <Box>
              <Flx ac jb sx={{ mb: 0.5 }}>
                <Txt
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    letterSpacing: 0.6,
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Activity & Comments{" "}
                  {comments.length > 0 && (
                    <Box
                      component="span"
                      sx={{ color: panel.textFaint, fontWeight: 400, ml: 0.5 }}
                    >
                      ({comments.length})
                    </Box>
                  )}
                </Txt>
                {!isEmpty(comments) && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={
                      <ChatBubbleOutline
                        sx={{ fontSize: "12px !important", mr: -0.5 }}
                      />
                    }
                    onClick={() => setCommentModalOpen(true)}
                    sx={{
                      fontSize: 11,
                      // color: panel.textMuted,
                      textTransform: "none",
                      minWidth: 0,
                      fontWeight: 400,
                      p: 0.25,
                      px: 1,
                    }}
                  >
                    Add comment
                  </Button>
                )}
              </Flx>

              {comments.length === 0 ? (
                <Flx g={1} column sx={{ alignItems: "flex-start" }}>
                  <Txt
                    sx={{
                      fontSize: 12,
                      color: panel.textFaint,
                      fontStyle: "italic",
                    }}
                  >
                    No comments yet — click "Add comment" to start a thread.
                  </Txt>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      <ChatBubbleOutline
                        sx={{ fontSize: "12px !important", mr: -0.5 }}
                      />
                    }
                    onClick={() => setCommentModalOpen(true)}
                    sx={{
                      fontSize: 11,
                      // color: panel.textMuted,
                      textTransform: "none",
                      minWidth: 0,
                      fontWeight: 400,
                      p: 0.25,
                      px: 1,
                    }}
                  >
                    Add comment
                  </Button>
                </Flx>
              ) : (
                // <Box
                //   sx={{
                //     border: `1px dashed ${panel.border}`,
                //     borderRadius: 1.5,
                //     px: 1.5,
                //     py: 2,
                //     textAlign: "center",
                //   }}
                // >
                //   <Txt
                //     sx={{
                //       fontSize: 12,
                //       color: panel.textFaint,
                //       fontStyle: "italic",
                //     }}
                //   >
                //     No comments yet — click "Add comment" to start a thread.
                //   </Txt>
                // </Box>
                <Flx column g={1}>
                  {comments.map((c, i) => (
                    <CommentRow
                      key={c?._id || `c-${i}`}
                      comment={c}
                      users={users}
                    />
                  ))}
                </Flx>
              )}
            </Box>
          </Flx>

          <DetailCard
            title="Properties"
            endAdornment={
              <InlineEditButton
                onClick={() => onEdit?.(data)}
                label="Edit Exception Details"
              />
            }
          >
            {/* Group A — Classification */}
            <Flx column g={0.75}>
              <DetailRow
                label="Type"
                value={isHardException ? "Hard" : "Soft"}
              />
              {data?.exceptionCategory && (
                <DetailRow label="Category" value={data.exceptionCategory} />
              )}
              {data?.riskBucket && (
                <TooltipRow
                  label="Risk Bucket"
                  value={data.riskBucket}
                  tooltip={RISK_BUCKET_DESCRIPTIONS[data.riskBucket]}
                />
              )}
              {data?.exceptionStatus && (
                <DetailRow label="Status" value={data.exceptionStatus} />
              )}
            </Flx>

            {/* Group B — People */}
            <PropertiesGroup
              show={
                !!data?.assignedTo ||
                !!data?.createdBy ||
                !!data?.updatedBy ||
                !!data?.resolvedBy ||
                !!data?.exceptionApprovalBy ||
                !!data?.postCloseFollowUpContact
              }
            >
              {data?.assignedTo && (
                <DetailRow
                  label="Assigned to"
                  value={getUserFullNameFromEmail(users, data.assignedTo)}
                />
              )}
              {data?.createdBy && (
                <DetailRow
                  label="Created by"
                  value={getUserFullNameFromEmail(users, data.createdBy)}
                />
              )}
              {data?.updatedBy && (
                <DetailRow
                  label="Updated by"
                  value={getUserFullNameFromEmail(users, data.updatedBy)}
                />
              )}
              {data?.resolvedBy && (
                <ResolvedByRow resolvedBy={data.resolvedBy} />
              )}
              {data?.exceptionApprovalBy && !data?.resolvedBy && (
                <DetailRow
                  label="Approved by"
                  value={getUserFullNameFromEmail(
                    users,
                    data.exceptionApprovalBy,
                  )}
                />
              )}
              {data?.postCloseFollowUpContact && (
                <DetailRow
                  label="Post-close follow-up"
                  value={
                    data.postCloseFollowUpContact?.fullName ||
                    data.postCloseFollowUpContact?.emailAddress ||
                    "—"
                  }
                />
              )}
            </PropertiesGroup>

            {/* Group C — Dates */}
            <PropertiesGroup
              show={
                !!data?.createDateTime ||
                !!data?.exceptionDate ||
                !!data?.resolutionDateTime ||
                !!updatedLabel
              }
            >
              {(data?.createDateTime || data?.exceptionDate) && (
                <DetailRow
                  label="Created"
                  value={formatDate(data.createDateTime || data.exceptionDate)}
                />
              )}
              {data?.resolutionDateTime && (
                <DetailRow
                  label="Resolved"
                  value={formatDate(data.resolutionDateTime)}
                />
              )}
              {updatedLabel && (
                <DetailRow label="Updated" value={updatedLabel} />
              )}
            </PropertiesGroup>

            {/* Group D — Notification flags / toggles */}
            <PropertiesGroup show>
              <ToggleRow
                label="Email on changes"
                icon={<MailOutline sx={{ fontSize: 12 }} />}
                checked={!!data?.sendEmail}
                tooltip={data?.sendEmail ? EMAIL_ON_TOOLTIP : EMAIL_OFF_TOOLTIP}
              />
              {isHardException && (
                <ToggleRow
                  label="Blocks deal as structured"
                  icon={<NotificationsActiveOutlined sx={{ fontSize: 12 }} />}
                  checked={!!data?.notifyDealCannotProceedAsStructured}
                  tooltip={BLOCKS_DEAL_TOOLTIP}
                  accent={amber}
                />
              )}
              {isHardException && data?.notifyDealDead && (
                <ToggleRow
                  label="Deal dead"
                  icon={<NotificationsActiveOutlined sx={{ fontSize: 12 }} />}
                  checked={!!data?.notifyDealDead}
                  tooltip={DEAL_DEAD_TOOLTIP}
                  accent={red}
                />
              )}
            </PropertiesGroup>
          </DetailCard>
        </Box>
      </Flx>
      <Divider sx={{ borderColor: "#e1e4e8", borderWidth: "1px", mb: "0px" }} />
      <CommentModal
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        commentType={COMMENT_TYPES.EXCEPTION}
        context={{ dealId, exceptionId: data?._id }}
        comments={comments}
        title="Add Exception Comment"
      />

      {fieldEditKey && FIELD_EDIT_CONFIGS[fieldEditKey] && (
        <FieldUpdateModal
          open={!!fieldEditKey}
          onClose={() => setFieldEditKey(null)}
          updateType={UPDATE_TYPES.EXCEPTION}
          context={{ dealId, exceptionId: data?._id }}
          title={FIELD_EDIT_CONFIGS[fieldEditKey].title}
          description={FIELD_EDIT_CONFIGS[fieldEditKey].description}
          fields={FIELD_EDIT_CONFIGS[fieldEditKey].fields}
          initialValues={FIELD_EDIT_CONFIGS[fieldEditKey].fields.reduce(
            (acc, f) => ({ ...acc, [f.name]: data?.[f.name] ?? "" }),
            {},
          )}
        />
      )}
    </Box>
  );
};

/* ─── Sub-components ─── */

const DetailCard = ({ title, endAdornment, children }) => (
  <Flx column sx={{ minWidth: 0 }}>
    <Flx ac jb sx={{ mt: 1 }}>
      <Txt
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: 0.6,
          color: "#64748b",
          textTransform: "uppercase",
        }}
      >
        {title}
      </Txt>
      {endAdornment}
    </Flx>
    <Box
      sx={{
        border: `1px solid ${panel.border}`,
        background: "#fff",
        borderRadius: 1.5,
        p: 1.5,
        minWidth: 0,
        whiteSpace: "normal",
        overflow: "hidden",
      }}
    >
      <Txt>{children}</Txt>
    </Box>
  </Flx>
);

// Small Edit button used in DetailCard `endAdornment` slots
const InlineEditButton = ({ onClick, label = "Edit" }) => (
  <Button
    size="small"
    variant="text"
    startIcon={<EditOutlined sx={{ fontSize: "12px !important", mr: -0.5 }} />}
    onClick={onClick}
    sx={{
      fontSize: 11,
      // color: panel.textMuted,
      textTransform: "none",
      fontWeight: 400,
      minWidth: 0,
      p: 0.5,
      px: 1,
    }}
  >
    {label}
  </Button>
);

const DetailRow = ({ label, value }) => (
  <Flx ac jb g={1}>
    <Txt sx={{ fontSize: 11.5, color: panel.textLabel }}>{label}</Txt>
    <Txt sx={{ fontSize: 11.5, color: panel.text, textAlign: "right" }}>
      {value || "—"}
    </Txt>
  </Flx>
);

// DetailRow variant with a help tooltip on the label
const TooltipRow = ({ label, value, tooltip }) => (
  <Flx ac jb g={1}>
    <Tooltip
      title={tooltip || ""}
      arrow
      placement="top"
      disableHoverListener={!tooltip}
    >
      <Txt
        sx={{
          fontSize: 11.5,
          color: panel.textLabel,
          borderBottom: tooltip ? `1px dotted ${panel.textFaint}` : "none",
          cursor: tooltip ? "help" : "default",
        }}
      >
        {label}
      </Txt>
    </Tooltip>
    <Txt sx={{ fontSize: 11.5, color: panel.text, textAlign: "right" }}>
      {value || "—"}
    </Txt>
  </Flx>
);

// Wraps a logical group with a hairline divider above it. Skips render when empty.
const PropertiesGroup = ({ show, children }) => {
  if (!show) return null;
  return (
    <Box
      sx={{
        mt: 1.25,
        pt: 1.25,
        borderTop: `1px solid ${panel.borderSubtle}`,
      }}
    >
      <Flx column g={0.75}>
        {children}
      </Flx>
    </Box>
  );
};

// resolvedBy is an object: { fullName, emailAddress, roles, completionDateTime }
const ResolvedByRow = ({ resolvedBy }) => {
  const display = resolvedBy?.fullName || resolvedBy?.emailAddress || "—";
  const roles = resolvedBy?.roles;
  const tooltip = resolvedBy?.emailAddress || "";
  return (
    <Flx ac jb g={1}>
      <Txt sx={{ fontSize: 11.5, color: panel.textLabel }}>Resolved by</Txt>
      <Tooltip
        title={tooltip}
        arrow
        placement="top"
        disableHoverListener={!tooltip}
      >
        <Txt
          sx={{
            fontSize: 11.5,
            color: panel.text,
            textAlign: "right",
            cursor: tooltip ? "help" : "default",
          }}
        >
          {display}
          {roles ? (
            <Box
              component="span"
              sx={{ color: panel.textLabel, fontWeight: 400, ml: 0.5 }}
            >
              ({roles})
            </Box>
          ) : null}
        </Txt>
      </Tooltip>
    </Flx>
  );
};

// Read-only status indicator — clearly informational (not a control).
// Renders a filled-circle "On" or hollow-circle "Off" pill with a tinted label.
const StatusIndicator = ({ checked, accent }) => {
  const onColor = accent?.[600] || "#059669";
  const offColor = panel.textFaint;
  const Icon = checked ? CheckBoxRounded : DisabledByDefaultRounded;
  return (
    <Box sx={{ flexShrink: 0, display: "inline-flex" }}>
      <Icon
        sx={{
          fontSize: 16,
          color: checked ? onColor : offColor,
        }}
      />
    </Box>
  );
};

// Row showing a labeled boolean flag with a non-interactive status indicator.
// The tooltip (if provided) attaches ONLY to the label text — hovering the
// indicator or empty row space does not fire it.
const ToggleRow = ({ label, icon, checked, tooltip, accent }) => {
  const labelTxt = (
    <Txt
      component="span"
      sx={{
        fontSize: 11.5,
        color: checked ? panel.textBody : panel.textLabel,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        borderBottom: tooltip ? `1px dotted ${panel.textFaint}` : "none",
        cursor: tooltip ? "help" : "default",
      }}
    >
      {label}
    </Txt>
  );

  return (
    <Flx ac jb g={1} sx={{ minWidth: 0 }}>
      <Flx ac g={0.5} sx={{ minWidth: 0 }}>
        {icon ? (
          <Box
            component="span"
            sx={{
              color: checked
                ? accent?.[700] || panel.textMuted
                : panel.textFaint,
              display: "inline-flex",
            }}
          >
            {icon}
          </Box>
        ) : null}
        {tooltip ? (
          <Tooltip title={tooltip} arrow placement="top">
            {labelTxt}
          </Tooltip>
        ) : (
          labelTxt
        )}
      </Flx>
      <StatusIndicator checked={checked} accent={accent} />
    </Flx>
  );
};

const Avatar = ({ name }) => {
  const initials = useMemo(() => {
    if (!name) return "?";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [name]);

  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: panel.avatarBg,
        color: panel.avatarFg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </Box>
  );
};

const CommentRow = ({ comment, users }) => {
  // Exception comments use postedBy + commentDateTime (per EXCEPTIONS.md §8.1).
  const authorEmail =
    comment?.postedBy ||
    comment?.submittedBy ||
    comment?.createdBy ||
    comment?.author;
  const author = authorEmail
    ? getUserFullNameFromEmail(users, authorEmail) || authorEmail
    : "Unknown";
  const createdAt =
    comment?.commentDateTime || comment?.createDateTime || comment?.createdAt;
  const body =
    comment?.htmlString ||
    comment?.commentBody ||
    comment?.body ||
    comment?.message ||
    "";

  return (
    <Flx g={1}>
      <Box sx={{ mt: 0.25 }}>
        <Avatar name={author} />
      </Box>
      <Box
        sx={{
          flex: 1,
          border: `1px solid ${panel.border}`,
          background: "#fff",
          borderRadius: 1.5,
          px: 1.5,
          py: 1,
        }}
      >
        <Flx ac g={1} sx={{ mb: 0.25 }}>
          <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: panel.text }}>
            {author}
          </Txt>
          {createdAt && (
            <Txt sx={{ fontSize: 10.5, color: panel.textLabel }}>
              {formatDate(createdAt)}
            </Txt>
          )}
        </Flx>
        {body ? (
          <Box
            sx={{
              fontSize: 12.5,
              color: panel.textStrong,
              lineHeight: 1.5,
              "& p": { m: 0 },
            }}
            dangerouslySetInnerHTML={{ __html: body }}
          />
        ) : (
          <Txt
            sx={{ fontSize: 12, color: panel.textFaint, fontStyle: "italic" }}
          >
            (no content)
          </Txt>
        )}
      </Box>
    </Flx>
  );
};

export default LoanDrilldownExceptionsRedesignTab;
